// ============================================================
// ✅ EmployesListe.jsx - Liste des employés
// Support multi-tenant : affichage société + filtre
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { 
  Plus, Edit, Trash2, Search, 
  CheckCircle, XCircle, Users, UserCheck, 
  UserCog, TrendingUp, Globe, ChevronLeft, ChevronRight
} from 'lucide-react';
import EmployeModal from './EmployeModal';
import EditEmployeModal from './EditEmployeModal';
import DeleteConfirmModal from './DeleteConfirmModal';

export default function EmployesListe() {
  
  // ==========================================================
  // 📊 ÉTATS
  // ==========================================================
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeToDelete, setEmployeToDelete] = useState(null);
  
  const [stats, setStats] = useState({ total: 0, managers: 0, admins: 0, actifs: 0 });

  // 👑 Contexte utilisateur
  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  // ==========================================================
  // 🔄 CHARGEMENT
  // ==========================================================

  // ✅ Correction : useCallback pour fetchEmployes
  const fetchEmployes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/employes', {
        params: { ID_Societe: isSuperAdmin ? null : user?.ID_Societe }
      });
      setEmployes(res.data.employes || []);
    } catch (err) {
      console.error('Erreur chargement employés:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user?.ID_Societe]); // ✅ Dépendances

  // ✅ Correction : ajout de fetchEmployes dans les dépendances
  useEffect(() => { 
    fetchEmployes(); 
  }, [fetchEmployes]); // ✅ Dépendance corrigée

  // ✅ Correction : useCallback pour calculateStats
  const calculateStats = useCallback(() => {
    setStats({
      total: employes.length,
      managers: employes.filter(e => e.Role === 'manager').length,
      admins: employes.filter(e => e.Role === 'admin').length,
      actifs: employes.filter(e => e.EstActif === 1 || e.EstActif === true).length
    });
  }, [employes]); // ✅ Dépendance

  // ✅ useEffect pour les stats
  useEffect(() => { 
    calculateStats(); 
  }, [calculateStats]); // ✅ Dépendance corrigée

  // ==========================================================
  // 🖱️ HANDLERS
  // ==========================================================
  const handleEditClick = (emp) => { setSelectedEmploye(emp); setShowEditModal(true); };
  const handleDeleteClick = (emp) => { setEmployeToDelete(emp); setShowDeleteModal(true); };

  // ==========================================================
  // 📊 FILTRES
  // ==========================================================
  const filteredEmployes = employes.filter(emp =>
    emp.Nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.Prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.Matricule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmployes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployes.length / itemsPerPage);

  // ==========================================================
  // 🎨 BADGES
  // ==========================================================
  const getStatusBadge = (estActif) => {
    // ✅ Correction : utiliser === au lieu de ==
    if (estActif === 1 || estActif === true) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700"><CheckCircle size={10} /> Actif</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500"><XCircle size={10} /> Inactif</span>;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">Admin</span>;
      case 'manager': return <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700">Manager</span>;
      default: return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">User</span>;
    }
  };

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="p-3 md:p-6 bg-gray-50 min-h-screen">
      
      {/* En-tête */}
      <div className="mb-3 md:mb-4">
        <h2 className="text-base md:text-lg font-bold text-gray-800">👥 Employés</h2>
        <p className="text-[11px] text-gray-400">
          Gestion des ressources humaines {user?.SocieteNom ? `• ${user.SocieteNom}` : ''}
        </p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Total</p><p className="text-lg font-bold text-gray-800">{stats.total}</p></div>
            <Users size={18} className="text-emerald-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Managers</p><p className="text-lg font-bold text-emerald-600">{stats.managers}</p></div>
            <UserCheck size={18} className="text-emerald-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Admins</p><p className="text-lg font-bold text-purple-600">{stats.admins}</p></div>
            <UserCog size={18} className="text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div><p className="text-[10px] text-gray-400">Actifs</p><p className="text-lg font-bold text-green-600">{stats.actifs}</p></div>
            <TrendingUp size={18} className="text-green-500" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" placeholder="Rechercher..."
            className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg w-full sm:w-60 text-xs focus:ring-2 focus:ring-emerald-500"
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs flex items-center justify-center gap-1 transition shadow-sm">
          <Plus size={12} /> Nouvel employé
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-500">
                <th className="px-3 py-2 text-left">Matricule</th>
                <th className="px-3 py-2 text-left">Nom & Prénom</th>
                <th className="px-3 py-2 text-left hidden sm:table-cell">Email</th>
                <th className="px-3 py-2 text-center">Rôle</th>
                {/* ✅ Colonne Société (SuperAdmin) */}
                {isSuperAdmin && (
                  <th className="px-3 py-2 text-left hidden lg:table-cell">Société</th>
                )}
                <th className="px-3 py-2 text-center hidden lg:table-cell">Statut</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={isSuperAdmin ? 7 : 6} className="px-3 py-8 text-center text-gray-400 text-xs">Chargement...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan={isSuperAdmin ? 7 : 6} className="px-3 py-8 text-center text-gray-400 text-xs">Aucun employé</td></tr>
              ) : (
                currentItems.map(emp => (
                  <tr key={emp.ID_Employes} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{emp.Matricule}</td>
                    <td className="px-3 py-2">{emp.Prenom} {emp.Nom}</td>
                    <td className="px-3 py-2 hidden sm:table-cell text-gray-500">{emp.Email}</td>
                    <td className="px-3 py-2 text-center">{getRoleBadge(emp.Role)}</td>
                    {/* ✅ Colonne Société */}
                    {isSuperAdmin && (
                      <td className="px-3 py-2 hidden lg:table-cell">
                        <span className="inline-flex items-center gap-1 text-gray-500">
                          <Globe size={10} />
                          {emp.RaisonSociale || emp.SocieteNom || `ID: ${emp.ID_Societe}`}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 text-center hidden lg:table-cell">{getStatusBadge(emp.EstActif)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEditClick(emp)} className="p-1 text-blue-500 hover:text-blue-700 transition"><Edit size={14} /></button>
                        <button onClick={() => handleDeleteClick(emp)} className="p-1 text-red-500 hover:text-red-700 transition"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
            <p className="text-[10px] text-gray-400">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredEmployes.length)} sur {filteredEmployes.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={14} /></button>
              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      <EmployeModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={fetchEmployes} />
      <EditEmployeModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedEmploye(null); }} onSuccess={fetchEmployes} employe={selectedEmploye} />
      <DeleteConfirmModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setEmployeToDelete(null); }} onSuccess={fetchEmployes} employe={employeToDelete} />
    </div>
  );
}